import { computed, getCurrentScope, onScopeDispose, ref } from 'vue'

/* ── Live-chat hours, in the reader's own clock ───────────────────────────
   The hours are staffed from Amsterdam, but "9 AM to 5 PM CEST" makes every
   customer outside that zone do arithmetic — and get it wrong twice a year,
   because CEST is only half true: Amsterdam is CET for five months of the year,
   so the label was wrong from late October to late March.

   Everything below is derived from one instant instead. `Intl` is given the
   IANA zone rather than a fixed offset, so DST is the browser's problem, and
   the times are printed with the visitor's own locale and clock convention —
   a US visitor sees 3:00 PM where a German one sees 15:00, from the same code.

   Weekends are closed, so the badge is weekday-gated and "next open" walks
   forward to Monday rather than to tomorrow. The gate is applied on the
   Amsterdam wall-clock date, not the reader's: a visitor reading on Sunday
   evening in Auckland is already inside Amsterdam's Monday.

   This lives in a composable rather than on the Support page because the same
   hours have to appear on every surface that offers a chat — the Support page
   *and* the confirmation dialog every other entry point goes through. Two
   copies of this arithmetic would drift. */

const SUPPORT_TZ = 'Europe/Amsterdam'
const OPEN_HOUR = 9
const CLOSE_HOUR = 17

/* Minute resolution is enough for an open/closed badge, and it keeps the page
   from re-rendering once a second for a line that changes twice a day.

   The clock is shared at module scope and reference-counted: the Support page
   and the confirmation dialog can both be mounted, and neither should own a
   timer the other depends on. */
const now = ref(Date.now())
let timer = null
let consumers = 0

const retainClock = () => {
  consumers += 1
  if (timer === null) {
    now.value = Date.now()
    timer = setInterval(() => (now.value = Date.now()), 60_000)
  }
}

const releaseClock = () => {
  consumers = Math.max(0, consumers - 1)
  if (consumers === 0 && timer !== null) {
    clearInterval(timer)
    timer = null
  }
}

/** What `timeZone` reads on the clock at a given instant, as plain numbers. */
const partsInZone = (ms, timeZone) => {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  const p = Object.fromEntries(fmt.formatToParts(new Date(ms)).map((x) => [x.type, x.value]))
  // `hour12: false` yields 24 for midnight in some engines; 24 % 24 = 0.
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour % 24, min: +p.minute }
}

/**
 * The UTC instant at which `timeZone`'s wall clock reads the given date + hour.
 *
 * There is no direct API for this, so it is solved by iteration: guess that the
 * wall-clock time is UTC, measure how far off that guess lands in the target
 * zone, and correct. Run twice — a single pass is wrong for the one hour a year
 * when the correction itself crosses a DST boundary.
 */
const zonedToUtc = (y, m, d, hour, timeZone) => {
  let ms = Date.UTC(y, m - 1, d, hour, 0, 0)
  for (let i = 0; i < 2; i++) {
    const p = partsInZone(ms, timeZone)
    const seen = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min)
    ms -= seen - Date.UTC(y, m - 1, d, hour, 0, 0)
  }
  return ms
}

const localTime = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

/* A wall-clock date carries a weekday regardless of zone, so `Date.UTC` is used
   purely as a calendar here — no instant is implied by it. */
const isWorkday = (y, m, d) => {
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return dow >= 1 && dow <= 5
}

/**
 * Live-chat opening hours for the current reader, recomputed every minute.
 *
 * Returns a single computed object:
 *   isOpen  — staffed right now
 *   range   — "9:00 AM – 5:00 PM", already in the reader's zone and locale
 *   summary — the full one-liner: weekdays + range + weekend caveat
 *   badge   — "Open now" / "Closed"
 *   detail  — when it next closes, or when it next opens
 */
export function useSupportHours() {
  // Guard so the composable is still callable outside a component scope
  // (tests, or a plain module import) instead of leaking a timer.
  if (getCurrentScope()) {
    retainClock()
    onScopeDispose(releaseClock)
  }

  const supportHours = computed(() => {
    const at = now.value
    const there = partsInZone(at, SUPPORT_TZ)

    const opensToday = zonedToUtc(there.y, there.m, there.d, OPEN_HOUR, SUPPORT_TZ)
    const closesToday = zonedToUtc(there.y, there.m, there.d, CLOSE_HOUR, SUPPORT_TZ)

    const staffedToday = isWorkday(there.y, there.m, there.d)
    const isOpen = staffedToday && at >= opensToday && at < closesToday

    // Before opening on a staffed day it is later today; otherwise walk the *wall
    // clock* calendar forward to the next weekday. Adding days to the date rather
    // than 24h to the instant keeps 9 AM as 9 AM across a DST change, where
    // +86,400,000ms would drift it to 8 or 10.
    let nextOpen = staffedToday && at < opensToday ? opensToday : null
    for (let i = 1; nextOpen === null && i <= 7; i++) {
      const day = new Date(Date.UTC(there.y, there.m - 1, there.d + i))
      const [y, m, d] = [day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate()]
      if (isWorkday(y, m, d)) nextOpen = zonedToUtc(y, m, d, OPEN_HOUR, SUPPORT_TZ)
    }

    const untilMs = (isOpen ? closesToday : nextOpen) - at
    const mins = Math.max(0, Math.round(untilMs / 60_000))
    const days = Math.floor(mins / 1440)
    const h = Math.floor((mins % 1440) / 60)
    // A weekend wait is measured in days; "62h 10m" is technically true and useless.
    const rel = days >= 1 ? `${days}d ${h}h` : h >= 1 ? `${h}h ${mins % 60}m` : `${mins}m`

    // Naming the day only helps when it is not the reader's today, and the reader's
    // today is their own calendar date, not Amsterdam's.
    const sameLocalDay = new Date(nextOpen).toDateString() === new Date(at).toDateString()
    const whenOpen = sameLocalDay
      ? `at ${localTime(nextOpen)}`
      : `${new Date(nextOpen).toLocaleDateString([], { weekday: 'long' })} at ${localTime(nextOpen)}`

    // Printed from today's boundaries so the range reflects the reader's zone
    // on the day they are actually reading it, DST included.
    const range = `${localTime(opensToday)} – ${localTime(closesToday)}`

    return {
      isOpen,
      range,
      summary: `Live chat is staffed weekdays, ${range} your time — closed Saturday and Sunday.`,
      badge: isOpen ? 'Open now' : 'Closed',
      detail: isOpen
        ? `Closes at ${localTime(closesToday)} your time, in ${rel}.`
        : `Opens ${whenOpen} your time, in ${rel}.`,
    }
  })

  return { supportHours }
}
